import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  AcademicCapIcon,
  UserGroupIcon,
  ChevronRightIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { H1, H2, Body, Card, Button, Badge, Spinner } from '../components/ui';
import { Select } from '../components/forms';
import { useClassroomsWithStats } from '../hooks/api';

const Classrooms: React.FC = () => {
  const [selectedGrade, setSelectedGrade] = useState('all');

  const { data: classrooms = [], isLoading, error } = useClassroomsWithStats();

  // Build grade options from actual data
  const gradeMap = new Map<string, string>();
  classrooms.forEach((c) => {
    if (c.grade_name && !gradeMap.has(c.grade_id)) {
      gradeMap.set(c.grade_id, c.grade_name);
    }
  });
  const gradeOptions = [
    { value: 'all', label: 'All Grades' },
    ...Array.from(gradeMap.entries()).map(([id, name]) => ({ value: id, label: name })),
  ];

  // Filter classrooms
  const filteredClassrooms = selectedGrade === 'all'
    ? classrooms
    : classrooms.filter((c) => c.grade_id === selectedGrade);

  // Group by grade_id
  const classroomsByGrade = filteredClassrooms.reduce((acc, classroom) => {
    const key = classroom.grade_id;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(classroom);
    return acc;
  }, {} as Record<string, typeof classrooms>);

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="text-center py-12">
          <AcademicCapIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <H2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Unable to load classrooms
          </H2>
          <Body className="text-gray-600 dark:text-gray-400">
            Please check your connection and try again.
          </Body>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <H1 className="mb-2">Classrooms</H1>
          <Body className="text-gray-600 dark:text-gray-400">
            Manage your classrooms and view child progress across all grades.
          </Body>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 max-w-xs">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Grade Level
          </label>
          <Select
            value={selectedGrade}
            onChange={(e) => setSelectedGrade(e.target.value)}
            options={gradeOptions}
          />
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
        <Card className="p-4 text-center">
          <div className="text-2xl font-bold text-primary-600">{classrooms.length}</div>
          <Body className="text-sm text-gray-500 dark:text-gray-400">Classrooms</Body>
        </Card>
        <Card className="p-4 text-center">
          <div className="text-2xl font-bold text-green-600">
            {classrooms.reduce((sum, c) => sum + (c.stats?.active_children ?? 0), 0)}
          </div>
          <Body className="text-sm text-gray-500 dark:text-gray-400">Children</Body>
        </Card>
        <Card className="p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">
            {classrooms.reduce((sum, c) => sum + (c.stats?.total_observations ?? 0), 0)}
          </div>
          <Body className="text-sm text-gray-500 dark:text-gray-400">Observations</Body>
        </Card>
        <Card className="p-4 text-center">
          <div className="text-2xl font-bold text-purple-600">
            {classrooms.reduce((sum, c) => sum + (c.stats?.total_assessments ?? 0), 0)}
          </div>
          <Body className="text-sm text-gray-500 dark:text-gray-400">Assessments</Body>
        </Card>
      </div>

      {/* Classrooms by Grade */}
      <div className="space-y-8">
        {Object.entries(classroomsByGrade).map(([gradeId, gradeClassrooms]) => (
          <motion.div
            key={gradeId}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <H2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mr-3">
                  {gradeClassrooms[0]?.grade_name || gradeId}
                </H2>
                <Badge variant="secondary" size="sm">
                  {gradeClassrooms.length} classroom{gradeClassrooms.length !== 1 ? 's' : ''}
                </Badge>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {gradeClassrooms.map((classroom, index) => (
                <motion.div
                  key={classroom.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Card className="p-6 hover:shadow-lg transition-all duration-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg mr-3">
                          <AcademicCapIcon className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                          <H2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            {classroom.name}
                          </H2>
                        </div>
                      </div>
                      <Badge variant={classroom.is_active ? 'success' : 'warning'} size="sm">
                        {classroom.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>

                    {/* Quick Stats */}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
                        <UserGroupIcon className="w-4 h-4 text-green-500 mx-auto mb-1" />
                        <Body className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          {classroom.stats?.active_children ?? '—'}
                        </Body>
                        <Body className="text-xs text-gray-500 dark:text-gray-400">Children</Body>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
                        <ClipboardDocumentListIcon className="w-4 h-4 text-blue-500 mx-auto mb-1" />
                        <Body className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          {classroom.stats?.total_observations ?? '—'}
                        </Body>
                        <Body className="text-xs text-gray-500 dark:text-gray-400">Observations</Body>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
                        <DocumentTextIcon className="w-4 h-4 text-purple-500 mx-auto mb-1" />
                        <Body className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          {classroom.stats?.total_assessments ?? '—'}
                        </Body>
                        <Body className="text-xs text-gray-500 dark:text-gray-400">Assessments</Body>
                      </div>
                    </div>

                    {/* Action Button */}
                    <Link to={`/classrooms/${classroom.slug || classroom.id}`}>
                      <Button
                        variant="primary"
                        size="sm"
                        fullWidth
                        className="flex items-center justify-center"
                      >
                        View Classroom
                        <ChevronRightIcon className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Empty State */}
      {filteredClassrooms.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <AcademicCapIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <H2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No classrooms found
          </H2>
          <Body className="text-gray-600 dark:text-gray-400">
            {selectedGrade === 'all'
              ? 'No classrooms have been created yet.'
              : 'No classrooms match your selected filter.'}
          </Body>
        </div>
      )}
    </div>
  );
};

export default Classrooms;
